import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaCheckCircle } from 'react-icons/fa';
import PageMetaLoader from '../components/PageMetaLoader';
import ContactAnimation from '../components/ContactAnimation';
import { en, km } from './ContactPage.data';
import { useI18n } from '../i18n';

const ContactPage = () => {
  const { locale } = useI18n();
  const t = locale === 'km' ? km : en;
  const form = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    // industry: '',
    companySize: '',
    subject: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Prepare data for the API
      const apiData = {
        FirstName: formData.name,
        LastName: '',
        PhoneNumber: formData.phone,
        Email: formData.email,
        CompanyName: formData.company,
        NoOfEmployees: formData.companySize ? { id: formData.companySize, name: formData.companySize } : null,
        Subject: formData.subject,
        Message: formData.message,
        NoOfEmployeesId: formData.companySize || '',
      };

      // Remove null fields
      Object.keys(apiData).forEach(key => apiData[key] === null && delete apiData[key]);

      // Use different API endpoints and credentials based on environment
      const isDev = process.env.NODE_ENV === 'development';
      const apiEndpoint = isDev
        ? process.env.REACT_APP_DEV_API_URL || 'http://localhost:6600/api/FlexLists/1/records'
        : process.env.REACT_APP_PROD_API_URL || 'https://admin.akas.co/api/FlexLists/2/records';
      
      const apiKey = isDev
        ? process.env.REACT_APP_DEV_API_KEY
        : process.env.REACT_APP_PROD_API_KEY;
      
      const apiSecret = isDev
        ? process.env.REACT_APP_DEV_API_SECRET
        : process.env.REACT_APP_PROD_API_SECRET;

      // Build headers
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };

      if (apiKey) {
        headers['X-API-Key'] = apiKey;
      }
      if (apiSecret) {
        headers['X-API-Secret'] = apiSecret;
      }

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(apiData),
      });

      const data = await response.json();

      if (response.ok) {
        setFormSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          // industry: '',
          companySize: '',
          subject: '',
          message: ''
        });
      } else {
        setError(data.message || data.error || t.formError);
      }
    } catch (err) {
      console.error('Contact form error:', err);
      setError(t.formError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormSubmitted(false);
    setError('');
  };

  const contactForm = <ContactForm ref={form} onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <label htmlFor="name">{t.form.fullName}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                  />
                </FormGroup>
                
                
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="company">{t.form.companyName}</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    autoComplete="organization"
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="companySize">{t.form.companySize}</label>
                  <select
                    id="companySize"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                  >
                    <option value="">{locale === 'km' ? 'ជ្រើសរើសចំនួនបុគ្គលិក' : 'Select Employee Count'}</option>
                    <option value="1-10">1 - 10</option>
                    <option value="11-50">11 - 50</option>
                    <option value="51-200">51 - 200</option>
                    <option value="201-500">201 - 500</option>
                    <option value="501-1000">501 - 1,000</option>
                    <option value="1001-5000">1,001 - 5,000</option>
                    <option value="5000+">5,000+</option>
                  </select>
                </FormGroup>
                
                {/* <FormGroup>
                  <label htmlFor="industry">{t.form.companyIndustry}</label>
                  <select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                  >
                    <option value="">Select Industry</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="retail">Retail / Wholesale</option>
                    <option value="hospitality">Hospitality / Restaurant</option>
                    <option value="healthcare">Healthcare / Medical</option>
                    <option value="education">Education / Training</option>
                    <option value="finance">Finance / Banking / Insurance</option>
                    <option value="construction">Construction / Real Estate</option>
                    <option value="technology">Technology / Software</option>
                    <option value="professional-services">Professional Services</option>
                    <option value="logistics">Logistics / Transportation</option>
                    <option value="agriculture">Agriculture</option>
                    <option value="ngo">NGO / Non-Profit</option>
                    <option value="government">Government / Public Sector</option>
                    <option value="other">Other</option>
                  </select>
                </FormGroup> */}
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label htmlFor="phone">{t.form.phoneNumber}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    autoComplete="tel"
                  />
                </FormGroup>
                <FormGroup>
                  <label htmlFor="email">{t.form.emailAddress}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                </FormGroup>
                
                
              </FormRow>

              <FormGroup>
                <label htmlFor="subject">{t.form.subject}</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="message">{t.form.message}</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </FormGroup>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? t.form.sending : t.form.submit}
              </SubmitButton>
            </ContactForm>

  return (
    <ContactContainer>
      <PageMetaLoader pageUrl="contact" />
      <PageHeader>
        <h1>{t.headerTitle}</h1>
        <p>{t.headerSubtitle}</p>
      </PageHeader>

      <ContactContent>
        <ContactInfo>
          <h2>{t.infoTitle}</h2>
          <p>{t.infoIntro}</p>
          
          <InfoItem>
            <IconWrapper>
              <FaMapMarkerAlt />
            </IconWrapper>
            <div>
              <h3>{t.addressLabel}</h3>
              <p>{t.address}</p>
            </div>
          </InfoItem>
          
          <InfoItem>
            <IconWrapper>
              <FaPhone size={20} />
            </IconWrapper>
            <div>
              <h3>{t.phoneLabel}</h3>
              <p><a href={`tel:${t.phone.replace(/\s+/g, '')}`}>{t.phone}</a></p>
            </div>
          </InfoItem>
          
          <InfoItem>
            <IconWrapper>
              <FaEnvelope size={20} />
            </IconWrapper>
            <div>
              <h3>{t.emailLabel}</h3>
              <p><a href={`mailto:${t.emailPrimary}`}>{t.emailPrimary}</a></p>
              <p><a href={`mailto:${t.emailSecondary}`}>{t.emailSecondary}</a></p>
            </div>
          </InfoItem>
          
          <InfoItem>
            <IconWrapper>
              <FaClock size={20} />
            </IconWrapper>
            <div>
              <h3>{t.businessHoursLabel}</h3>
              <p>{t.businessHours}</p>
            </div>
          </InfoItem>
        </ContactInfo>

        <ContactFormContainer>
          {formSubmitted ? (
            <SuccessMessage onClick={resetForm}>
              <SuccessIcon>
                <FaCheckCircle />
              </SuccessIcon>
              <h3>{t.form.successTitle}</h3>
              <p>{t.form.successText}</p>
              <p className="click-hint">{t.form.clickToSendAnother || 'Click to send another message'}</p>
            </SuccessMessage>
          ) : (<>
            {/* {contactForm} */}
            <ContactAnimation />
          </>
          
          )}
        </ContactFormContainer>
      </ContactContent>

      {/* <MapSection>
        <h2>{t.map.title}</h2>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d250273.72379094877!2d104.73672782345057!3d11.579761926152409!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3109513dc76a6be3%3A0x9c010ee85ab525bb!2sPhnom%20Penh!5e0!3m2!1sen!2skh!4v1650182044693!5m2!1sen!2skh"
          width="100%"
          height="450"
          style={{ border: 0, borderRadius: '10px' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={t.map.location}
        ></iframe>
      </MapSection> */}
    </ContactContainer>
  );
};

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h1 {
    font-size: clamp(2rem, 4vw, 2.75rem);
    color: var(--primary-color);
    margin-bottom: 1rem;
    font-weight: 700;
  }
  
  p {
    font-size: 1.15rem;
    max-width: 700px;
    margin: 0 auto;
    color: var(--text-muted);
    line-height: 1.6;
  }
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 3rem;
  margin-bottom: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  background: linear-gradient(135deg, var(--primary-color) 0%, #0077b3 100%);
  color: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 102, 204, 0.2);
  height: fit-content;
  
  h2 {
    font-size: 1.75rem;
    margin-bottom: 1rem;
    font-weight: 700;
  }
  
  > p {
    margin-bottom: 2rem;
    opacity: 0.9;
    line-height: 1.6;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.75rem;
  
  h3 {
    font-size: 1.1rem;
    margin-bottom: 0.35rem;
    font-weight: 600;
  }
  
  p {
    opacity: 0.95;
    margin: 0.15rem 0;
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  a {
    color: inherit;
    text-decoration: none;
    transition: opacity 0.2s;
    
    &:hover {
      opacity: 0.8;
      text-decoration: underline;
    }
  }
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  margin-right: 1rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
`;

const ContactFormContainer = styled.div`
  background-color: white;
  padding: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--border);
  
  h2 {
    font-size: 1.75rem;
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-weight: 700;
  }
  
  > p {
    margin-bottom: 2rem;
    color: var(--text-muted);
    line-height: 1.6;
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  
  label {
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
    font-size: 0.95rem;
  }
  
  input, textarea {
    padding: 0.875rem 1rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    transition: all 0.2s ease;
    background: var(--surface);
    color: var(--text-dark);
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.15);
    }
    
    &::placeholder {
      color: var(--text-muted);
      opacity: 0.6;
    }
    
    &:disabled {
      background: var(--border);
      cursor: not-allowed;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 120px;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '⚠';
    font-size: 1.1rem;
  }
`;

const SubmitButton = styled.button`
  background: var(--gradient-brand);
  color: white;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  align-self: flex-start;
  box-shadow: 0 4px 14px rgba(0, 102, 204, 0.3);
  
  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 102, 204, 0.4);
  }
  
  @media (max-width: 576px) {
    align-self: stretch;
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #a7f3d0;
  padding: 3rem 2rem;
  border-radius: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(16, 185, 129, 0.15);
  }
  
  h3 {
    color: #059669;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 700;
  }
  
  p {
    color: var(--text-dark);
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: var(--gradient-brand);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.75rem;
  box-shadow: 0 4px 14px rgba(0, 102, 204, 0.3);
`;

const MapSection = styled.div`
  margin-top: 2rem;
  
  h2 {
    font-size: 1.75rem;
    color: var(--primary-color);
    margin-bottom: 1.5rem;
    text-align: center;
    font-weight: 700;
  }
`;

export default ContactPage;